import React, { FC, useMemo, useState } from 'react';
import { Button } from '@chakra-ui/react';
import { useToggle } from 'react-use';
import { PairToken } from '@app/types/pool';
import { TransactionResultModal } from '@app/components/TransactionResultModal';
import { Vault } from '@app/hooks/plugins/useVaults';
import { Tooltip } from '@app/components/Tooltip';
import { StakeToNitroPoolModal } from '@app/screens/positions/components/SpNftControls/StakeToNitroPool/StakeToNitroPoolModal';
import { useNitroPool } from '@app/hooks/nitro/useNitroPool';
import { SiMediafire } from 'react-icons/si';
import { useConfig } from '@app/config';
import { HIDDEN_NITRO_POOLS } from '@app/constants/common';
import { QueryKeys } from '@app/constants/queryKeys';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  tokenDetails: PairToken | Vault;
  tokenId: string;
  deposit: string;
  poolAddress: string;
  onOpen?: (val: boolean) => void;
  showLabel?: boolean;
  nitroPoolId?: string;
}

const NOW = new Date().getTime();

export const StakeToNitroPool: FC<Props> = ({
  tokenDetails,
  tokenId,
  poolAddress,
  onOpen,
  showLabel,
  nitroPoolId,
  deposit
}) => {
  const [feedback, setFeedback] = useState<{
    status: 'success' | 'error';
    title: string;
    message: string;
    txHash?: string;
  } | null>(null);
  const config = useConfig();
  const [open, toggleOpen] = useToggle(false);
  const { data } = useNitroPool(poolAddress, nitroPoolId);
  const queryClient = useQueryClient();

  const activePool = useMemo(() => {
    if (!data) {
      return null;
    }

    if (!HIDDEN_NITRO_POOLS.includes(data.id) && NOW < +data.endTime * 1000) {
      return data;
    }

    return null;
  }, [data]);

  if (!activePool || !config?.FLAGS.showNitroPage) {
    return null;
  }

  return (
    <>
      <Tooltip tooltip="Stake to Nitro pool">
        <Button
          variant="secondary"
          onClick={e => {
            e.stopPropagation();
            toggleOpen(true);
            onOpen?.(true);
          }}
          _hover={{
            color: 'orange'
          }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            fontSize: '14px',
            width: '100%'
          }}
        >
          <SiMediafire size={16} color="var(--chakra-colors-nitro)" />
          {showLabel && 'Stake to Nitro'}
        </Button>
      </Tooltip>
      {open && (
        <StakeToNitroPoolModal
          isOpen
          onClose={() => {
            toggleOpen(false);
            onOpen?.(false);
          }}
          nitroPoolAddress={activePool?.id}
          tokenDetails={tokenDetails}
          tokenId={tokenId}
          poolAddress={poolAddress}
          deposit={deposit}
          onResult={res => setFeedback(res)}
        />
      )}
      <TransactionResultModal
        isOpen={!!feedback}
        status={feedback?.status}
        title={feedback?.title}
        message={feedback?.message}
        txHash={feedback?.txHash}
        onClose={async () => {
          await queryClient.invalidateQueries({
            queryKey: [QueryKeys.NITRO_POSITIONS_LIST]
          });

          await queryClient.invalidateQueries({
            queryKey: [QueryKeys.SP_NFTS_POSITIONS]
          });

          setFeedback(null);
          toggleOpen(false);
          onOpen?.(false);
        }}
      />
    </>
  );
};
